import React from 'react';
import { TrendingUp, Users, Clock, Calendar, BarChart2, GitBranch } from 'lucide-react';

const PredictiveAnalytics: React.FC = () => {
  // Mock data - in real app this would come from ML models analyzing sensor data
  const predictiveData = {
    overview: {
      accuracyScore: 92,
      predictedOccupancy: 385,
      forecastWindow: '4 weeks',
      activeModels: 6
    },
    occupancyPredictions: {
      daily: [
        { day: 'Monday', predicted: 385, confidence: 92 },
        { day: 'Tuesday', predicted: 392, confidence: 90 },
        { day: 'Wednesday', predicted: 378, confidence: 88 },
        { day: 'Thursday', predicted: 365, confidence: 91 },
        { day: 'Friday', predicted: 340, confidence: 89 }
      ],
      peakTimes: [
        { time: '9:00 AM', probability: 85, duration: '2 hours' },
        { time: '11:30 AM', probability: 78, duration: '1.5 hours' },
        { time: '2:00 PM', probability: 72, duration: '1 hour' }
      ]
    },
    resourceForecasts: {
      utilities: [
        {
          type: 'Electricity',
          predicted: '12,500 kWh',
          change: '+5%',
          factors: ['HVAC load', 'Equipment usage']
        },
        {
          type: 'Water',
          predicted: '2,800 gal',
          change: '-3%',
          factors: ['Occupancy', 'Weather forecast']
        },
        {
          type: 'HVAC',
          predicted: '8,200 BTU',
          change: '+8%',
          factors: ['Temperature', 'Humidity']
        }
      ],
      maintenance: [
        {
          system: 'HVAC Units',
          nextService: '15 days',
          confidence: 88,
          reason: 'Usage patterns'
        },
        {
          system: 'Lighting',
          nextService: '45 days',
          confidence: 92,
          reason: 'Age and usage'
        },
        {
          system: 'Plumbing',
          nextService: '30 days',
          confidence: 85,
          reason: 'Preventive maintenance'
        }
      ]
    },
    spaceUtilization: {
      trends: [
        {
          area: 'Open Office',
          currentUsage: 75,
          predictedChange: '+10%',
          recommendation: 'Consider expansion'
        },
        {
          area: 'Meeting Rooms',
          currentUsage: 65,
          predictedChange: '+15%',
          recommendation: 'Schedule optimization needed'
        },
        {
          area: 'Quiet Zones',
          currentUsage: 85,
          predictedChange: '+5%',
          recommendation: 'Near capacity'
        }
      ],
      patterns: [
        { time: '8 AM', current: 45, predicted: 52 },
        { time: '10 AM', current: 75, predicted: 82 },
        { time: '12 PM', current: 65, predicted: 70 },
        { time: '2 PM', current: 70, predicted: 75 },
        { time: '4 PM', current: 55, predicted: 58 }
      ]
    },
    anomalyPredictions: [
      {
        type: 'Traffic Pattern',
        probability: 85,
        impact: 'Medium',
        recommendation: 'Adjust HVAC scheduling'
      },
      {
        type: 'Resource Usage',
        probability: 92,
        impact: 'High',
        recommendation: 'Optimize lighting zones'
      },
      {
        type: 'Space Utilization',
        probability: 78,
        impact: 'Low',
        recommendation: 'Monitor for changes'
      }
    ]
  };

  const getChangeColor = (change: string) => {
    if (change.startsWith('+')) return 'text-yellow-500';
    if (change.startsWith('-')) return 'text-green-500';
    return 'text-gray-500';
  };

  const getImpactColor = (impact: string) => {
    switch (impact.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Predictive Analytics</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <TrendingUp className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Accuracy Score</h3>
          </div>
          <p className="text-3xl font-bold">{predictiveData.overview.accuracyScore}%</p>
          <p className="text-gray-500">Prediction accuracy</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Users className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Expected Peak</h3>
          </div>
          <p className="text-3xl font-bold">{predictiveData.overview.predictedOccupancy}</p>
          <p className="text-gray-500">People tomorrow</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Calendar className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Forecast Range</h3>
          </div>
          <p className="text-3xl font-bold">{predictiveData.overview.forecastWindow}</p>
          <p className="text-gray-500">Prediction window</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <GitBranch className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Active Models</h3>
          </div>
          <p className="text-3xl font-bold">{predictiveData.overview.activeModels}</p>
          <p className="text-gray-500">ML models running</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Occupancy Predictions</h3>
          <div className="space-y-4">
            {predictiveData.occupancyPredictions.daily.map((day, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{day.day}</h4>
                    <p className="text-sm text-gray-500">
                      Confidence: {day.confidence}%
                    </p>
                  </div>
                  <p className="font-medium">{day.predicted} people</p>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 rounded-full h-2"
                    style={{ width: `${day.confidence}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Resource Forecasts</h3>
          <div className="space-y-4">
            {predictiveData.resourceForecasts.utilities.map((utility, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{utility.type}</h4>
                    <p className="text-sm text-gray-500">
                      Factors: {utility.factors.join(', ')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{utility.predicted}</p>
                    <p className={`text-sm ${getChangeColor(utility.change)}`}>
                      {utility.change} expected
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Space Utilization Trends</h3>
          <div className="space-y-4">
            {predictiveData.spaceUtilization.trends.map((space, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{space.area}</h4>
                    <p className="text-sm text-gray-600">{space.recommendation}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{space.currentUsage}% current</p>
                    <p className={`text-sm ${getChangeColor(space.predictedChange)}`}>
                      {space.predictedChange} predicted
                    </p>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`rounded-full h-2 ${
                      space.currentUsage >= 85 ? 'bg-red-500' :
                      space.currentUsage >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${space.currentUsage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Predicted Anomalies</h3>
          <div className="space-y-4">
            {predictiveData.anomalyPredictions.map((anomaly, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{anomaly.type}</h4>
                    <p className="text-sm text-gray-600">{anomaly.recommendation}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${getImpactColor(anomaly.impact)}`}>
                    {anomaly.impact} Impact
                  </span>
                </div>
                <div className="flex items-center mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-purple-500 rounded-full h-2"
                      style={{ width: `${anomaly.probability}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">{anomaly.probability}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictiveAnalytics; 